import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { ITag } from '../../../types/interfaces';
const FilterEstatesComponent = lazy(() => import('../FilterEstatesComponent'));
const SEOHeadingComponent = lazy(() => import('../SEOHeadingComponent'));
const ObjectListComponent = lazy(() => import('./ObjectListComponent'));
const MonitorSearchFormForSalePage = lazy(
  () => import('~js/React/forms/MonitorSearchFormForSalePage')
);

import { ForSaleBrokerContext } from '../../../pages/Context/ForSaleBrokerContext';
import {
  FilterContext,
  filterToQuerystring,
  getFilterFromPath,
  getFilterFromQueryParams,
} from '../../../filterTools';
import { useParams } from 'react-router-dom';
import { getSessionId } from '~js/React/helpers';
import useUserContactId from '../../CustomHooks/useUserContactId';
import Cookies from 'js-cookie';
import { SearchFieldFallback } from '../Fallbacks/SearchFieldFallback';
import useMediaQuery from '../../CustomHooks/useMediaQuery';
import { Estates, FilterTypes, ObjectListLayout } from '~js/React/Enums';
import SearchField from '../SearchFieldComponent';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import CreateSearchProfileBtn from './CreateSearchProfileBtn';
import useSiteHeaderHeight from '../../CustomHooks/useSiteHeaderHeight';

const basename = '/till-salu/';

interface Props {
  stickyTitle?: string;
  stickyText?: string;
}

export const ForSaleComponentInner = ({
  stickyTitle,
  stickyText,
}: Props): JSX.Element => {
  const [tags, setTags] = useState<ITag[]>([]);
  const [visibleExtraCrits, setVisibleExtraCrits] = useState<{
    [key: string]: ITag;
  }>({});
  const [isAuthorizedBroker, setIsAuthorizedBroker] = useState<boolean>(false);
  const [showErrorForSale, setShowErrorForSale] = useState<boolean>(false);
  const [badFarmCombo, setBadFarmCombo] = useState(false);
  const [missingSearchParams, setMissingSearchParams] = useState(false);
  const [forbiddenEstateType, setForbiddenEstateType] = useState(false);
  const scrollLockUntilTagChange = useRef(false);
  const filterItemClicked = useRef(false);
  const path = useParams<Record<string, string>>();
  const sessionId = getSessionId();
  const { data: user } = useUserContactId(sessionId);
  const isBigScreen = useMediaQuery('(min-width: 64rem)');
  const isMediumScreen = useMediaQuery('(min-width: 46rem)');
  const isSmallScreen = useMediaQuery('(max-width: 55.9rem)');
  const [showHideModal, setShowHideModal] = useState(false);
  const [objectListLayout, setObjectListLayout] = useState<string>('row');
  const [showMapFilter, setShowMapFilter] = useState<boolean>(false);
  const menuHeight = useSiteHeaderHeight();
  const topMargin = menuHeight > 50 ? 'top-[56px]' : 'top-[48px]';
  const heightForMobileMapContainer =
    menuHeight > 50 ? 'h-[calc(100dvh-56px)]' : 'h-[calc(100dvh-48px)]';
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    var loginType = Cookies.get('loginType');
    setIsAuthorizedBroker(loginType === '2');
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return;
      const rect = scrollRef.current.getBoundingClientRect();

      if (rect.top <= -200 && objectListLayout === 'map' && isBigScreen) {
        setShowHideModal(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showHideModal]);

  useEffect(() => {
    if ((showMapFilter && !isBigScreen) || (showHideModal && !isBigScreen)) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
    }

    return () => {
      document.documentElement.style.overflow = '';
    };
  }, [showMapFilter, isBigScreen, showHideModal]);

  //on filter change
  useEffect(() => {
    if (!filterItemClicked.current) return;

    scrollLockUntilTagChange.current = false;
    const searchFilter = filterToQuerystring(tags);
    // update url in browser
    const currentUrl = window.location.href;
    let viewSetting = '';
    if (
      currentUrl.indexOf(ObjectListLayout.Map) > -1 ||
      currentUrl.indexOf(ObjectListLayout.Row) > -1
    ) {
      if (currentUrl.indexOf(ObjectListLayout.Map) > -1) {
        viewSetting = '#' + ObjectListLayout.Map;
      } else {
        viewSetting = '#' + ObjectListLayout.Row;
      }
    }

    window.history.replaceState(
      null,
      '',
      basename + searchFilter + viewSetting
    );

    if (tags.length === 0) {
      setTags([
        {
          type: FilterTypes.AllEstates,
          isChecked: true,
          label: 'Alla bostäder',
        },
      ]);
    } else {
      var existingTags = tags.find((x) => x.isChecked === true);
      var allEstates = tags.find((x) => x.type === FilterTypes.AllEstates);
      if (!existingTags || existingTags === undefined) {
        if (allEstates === undefined) {
          setTags([
            ...tags,
            {
              type: FilterTypes.AllEstates,
              isChecked: true,
              label: 'Alla bostäder',
            },
          ]);
        } else {
          setTags(
            tags.map((x) => {
              if (x.type === FilterTypes.AllEstates) {
                x.isChecked = true;
              }
              return x;
            })
          );
        }
      } else {
        setTags(tags);
      }
    }
  }, [tags]);

  useEffect(() => {
    function callback(tags: ITag[]) {
      setTimeout(() => {
        setTags(tags);
      }, 100);
    }
    if (path.estateType) {
      getFilterFromPath(path as Record<string, string>).then(callback);
    } else {
      getFilterFromQueryParams().then(callback);
    }
  }, []);

  if (
    isSmallScreen === undefined &&
    isMediumScreen === undefined &&
    isBigScreen === undefined
  ) {
    return (
      <div className="top-0 left-0 z-[1003] absolute bg-white w-full h-full"></div>
    );
  }

  const handleShowLayout = (layout: string) => {
    setObjectListLayout(layout);
    setShowMapFilter(false);
    var url = window.location.href.split('#')[0];
    const newUrl = url + '#' + layout;
    window.location.href = newUrl;
  };

  return (
    <ForSaleBrokerContext.Provider
      value={{
        isAuthorizedBroker,
        showErrorForSale,
        setShowErrorForSale,
        setIsAuthorizedBroker,
      }}
    >
      <FilterContext.Provider
        value={{
          isSmallScreen,
          isBigScreen,
          isMediumScreen,
          tags,
          setTags: (newTags: ITag[]) => {
            filterItemClicked.current = true;
            setTags(newTags);
          },
          visibleExtraCrits,
          setVisibleExtraCrits: (newTags: { [key: string]: ITag }) => {
            filterItemClicked.current = true;
            setVisibleExtraCrits(newTags);
          },
          sessionId: sessionId,
          user: user,
        }}
      >
        <Suspense>
          <SEOHeadingComponent path={path as Record<string, string>} />
        </Suspense>
        <div
          ref={scrollRef}
          className="lg:flex lg:justify-between mx-auto max-w-10xl"
        >
          <div
            className={`lg:w-1/4 w-full lg:min-w-[25.8rem] ${
              objectListLayout == 'map' && !isBigScreen
                ? `${topMargin} ${
                    showMapFilter ? 'right-0 overflow-auto' : '-right-[100vw]'
                  } z-[1003] fixed ${heightForMobileMapContainer} w-[100vw]`
                : ''
            }`}
          >
            {!isBigScreen && (
              <SearchField
                stickyField={true}
                inputLabel={Estates.SearchPlaceholder}
                activeInput={false}
                countySelected={false}
                multipleEstateTypesSelected={false}
                brokerOrOfficeTypesSelected={false}
                commercialOrProject={false}
                showMapFilter={showMapFilter}
              />
            )}
            {objectListLayout !== 'map' && (
              <div className="lg:hidden flex justify-center pt-3 pb-2">
                <button
                  onClick={() => handleShowLayout('map')}
                  className="flex gap-2 text-blue700"
                >
                  <span className="whitespace-nowrap">Sök på karta</span>
                  <img
                    src="/Images/Icons/map-pin-line-blue.svg"
                    alt="Sök på karta"
                  />
                </button>
              </div>
            )}

            {objectListLayout === 'map' && !isBigScreen && showMapFilter && (
              <div className="bottom-0 left-0 z-[1005] fixed flex justify-center gap-6 bg-white py-2 w-full">
                <button
                  onClick={() => {
                    handleShowLayout('row');
                    setTimeout(() => {
                      const element = document.querySelector(
                        '#object-content-view'
                      );
                      if (element && isSmallScreen) {
                        element.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        });
                      }
                    }, 40);
                  }}
                  className="flex justify-center items-center gap-3 bg-blue700 py-2 rounded-3xl w-full max-w-micro text-white"
                >
                  <span>Lista</span>
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-[14px]"
                  >
                    <rect width="19" height="8" fill="white" />
                    <rect y="11" width="19" height="8" fill="white" />
                  </svg>
                </button>
                <button
                  className="flex justify-center items-center gap-3 bg-blue700 py-2 rounded-3xl w-full max-w-micro text-white"
                  onClick={() => setShowMapFilter(false)}
                >
                  <span>Karta</span>
                  <img
                    className="w-5 h-auto -translate-y-[1px]"
                    src="/Images/Icons/map-pin-line-white.svg"
                    alt="Visa karta"
                  />
                </button>
              </div>
            )}
            <Suspense fallback={<SearchFieldFallback />}>
              <FilterEstatesComponent
                setShowHideModal={setShowHideModal}
                objectListLayout={objectListLayout}
                handleShowLayout={(layout) => handleShowLayout(layout)}
              />
              {objectListLayout !== 'map' && (
                <div className="lg:hidden flex justify-center pb-5">
                  <button
                    onClick={() => setShowHideModal(true)}
                    className="flex justify-center items-center gap-2 bg-blue700 py-3 rounded-3xl w-full max-w-xxs text-white"
                  >
                    <span>Skapa bevakning</span>
                    <img
                      className="w-6 h-auto"
                      src="/Images/Icons/monitoring_white.svg"
                      alt="Skapa bevakning"
                    />
                  </button>
                </div>
              )}
              {!isBigScreen && objectListLayout !== 'map' && (
                <CreateSearchProfileBtn
                  sessionId={sessionId}
                  setShowHideModalFunction={setShowHideModal}
                />
              )}
            </Suspense>

            {showHideModal && (
              <div
                className={`${topMargin} lg:top-[64px] left-0 z-[1000] fixed w-full pointer-events-none`}
              >
                <div
                  className={`relative mx-auto max-w-10xl ${heightForMobileMapContainer} lg:h-[calc(100dvh-64px)]`}
                >
                  <div className="top-0 left-0 lg:left-[25.8rem] xxl2:left-[25%] absolute bg-white shadow-[100px_0_80px_rgba(0,0,0,0.3)] border-l-1 border-l-grey100 w-full lg:w-[411px] h-full overflow-x-hidden pointer-events-auto">
                    <button
                      title="Stäng"
                      className="top-4 right-2 z-40 absolute flex items-center self-center bg-white px-2 py-1 rounded-full focus:outline-none text-blue700 lg:hover:underline transition-all button"
                      type="button"
                      onClick={() => {
                        setShowHideModal(false);
                      }}
                    >
                      Stäng
                      <img
                        className="ml-2 w-5 h-auto rotate-180 transform"
                        src={'/Images/Icons/close.svg'}
                        alt="stäng ikon"
                      />
                    </button>
                    <div className="max-h-[100%] overflow-x-hidden overflow-y-auto">
                      <Suspense>
                        <MonitorSearchFormForSalePage
                          setShowHideModalFunction={setShowHideModal}
                          open={showHideModal}
                          hasAllReqParamsToSave={missingSearchParams}
                          forBiddenEstateTypes={forbiddenEstateType}
                          badFarmCombo={badFarmCombo}
                        />
                      </Suspense>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <ObjectListComponent
            stickyTitle={stickyTitle}
            stickyText={stickyText}
            badFarmCombo={badFarmCombo}
            setBadFarmCombo={setBadFarmCombo}
            missingSearchParams={missingSearchParams}
            setMissingSearchParams={setMissingSearchParams}
            setForbiddenEstateType={setForbiddenEstateType}
            forbiddenEstateType={forbiddenEstateType}
            setObjectListLayout={setObjectListLayout}
            objectListLayout={objectListLayout}
            setShowMapFilter={setShowMapFilter}
            setShowHideModal={setShowHideModal}
          />

          {showHideModal && (
            <HelmetProvider>
              <Helmet>
                <script>
                  {`
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'filter_klick',
                    eventInfo: {
                      buttonText: 'Bevaka sökning'
                    }
                  });
                `}
                </script>
              </Helmet>
            </HelmetProvider>
          )}
        </div>
      </FilterContext.Provider>
    </ForSaleBrokerContext.Provider>
  );
};
export default ForSaleComponentInner;
